/**
 * Composable for persisting calendar filters in browser localStorage
 * Saves and restores filter state across browser sessions and page refreshes
 */

// Storage configuration
const STORAGE_KEY = 'calendar-filter-settings'
const STORAGE_VERSION = 1 // Increment when storage format changes

/**
 * Debounce helper to avoid excessive localStorage writes
 * @param {Function} func - Function to debounce
 * @param {number} wait - Milliseconds to wait
 * @returns {Function} - Debounced function
 */
function debounce(func, wait) {
	let timeout
	return function executedFunction(...args) {
		const later = () => {
			clearTimeout(timeout)
			func(...args)
		}
		clearTimeout(timeout)
		timeout = setTimeout(later, wait)
	}
}

/**
 * Check if localStorage is available
 * @returns {boolean}
 */
function isLocalStorageAvailable() {
	try {
		const test = '__localStorage_test__'
		window.localStorage.setItem(test, test)
		window.localStorage.removeItem(test)
		return true
	} catch (e) {
		console.warn('localStorage is not available:', e)
		return false
	}
}

export function useLocalStorageFilterPersistence() {
	/**
	 * Save filter state to localStorage
	 * @param {FilterManager} filterManager - The filter manager instance
	 */
	const saveFiltersToLocalStorage = (filterManager) => {
		if (!isLocalStorageAvailable()) {
			return
		}

		try {
			// Serialize filter state using FilterManager's built-in method
			const filterState = filterManager.serialize()

			// Wrap with version for future compatibility
			const storage = {
				version: STORAGE_VERSION,
				filters: filterState,
				timestamp: new Date().toISOString()
			}

			window.localStorage.setItem(STORAGE_KEY, JSON.stringify(storage))
		} catch (error) {
			console.error('Failed to save filters to localStorage:', error)
			// Don't throw - fail silently to not disrupt user experience
		}
	}

	/**
	 * Create a debounced version of save function to avoid excessive writes
	 * @param {FilterManager} filterManager - The filter manager instance
	 * @param {number} delay - Debounce delay in milliseconds (default: 300ms)
	 * @returns {Function} - Debounced save function
	 */
	const createDebouncedSave = (filterManager, delay = 300) => {
		return debounce(() => saveFiltersToLocalStorage(filterManager), delay)
	}

	/**
	 * Load filter state from localStorage
	 * @returns {Object|null} - Deserialized filter state or null if not found/invalid
	 */
	const loadFiltersFromLocalStorage = () => {
		if (!isLocalStorageAvailable()) {
			return null
		}

		try {
			const stored = window.localStorage.getItem(STORAGE_KEY)

			if (!stored) {
				return null // No saved filters yet
			}

			const storage = JSON.parse(stored)

			// Validate storage structure
			if (!storage || typeof storage !== 'object') {
				console.warn('Invalid filter storage format - expected object')
				return null
			}

			// Check version compatibility
			if (storage.version !== STORAGE_VERSION) {
				console.warn(
					`Filter storage version mismatch - expected ${STORAGE_VERSION}, got ${storage.version}`
				)
				// Could implement migration logic here in the future
				return null
			}

			// Validate filters object exists
			if (!storage.filters || typeof storage.filters !== 'object') {
				console.warn('Invalid filter storage - missing filters object')
				return null
			}

			return storage.filters
		} catch (error) {
			console.error('Failed to load filters from localStorage:', error)
			// Clear corrupted data
			try {
				window.localStorage.removeItem(STORAGE_KEY)
			} catch (cleanupError) {
				console.error('Failed to clear corrupted localStorage:', cleanupError)
			}
			return null
		}
	}

	/**
	 * Clear all persisted filter data from localStorage
	 */
	const clearFilterPersistence = () => {
		if (!isLocalStorageAvailable()) {
			return
		}

		try {
			window.localStorage.removeItem(STORAGE_KEY)
		} catch (error) {
			console.error('Failed to clear filter persistence:', error)
		}
	}

	/**
	 * Check if there is persisted filter data
	 * @returns {boolean}
	 */
	const hasPersistedFilters = () => {
		if (!isLocalStorageAvailable()) {
			return false
		}

		try {
			const stored = window.localStorage.getItem(STORAGE_KEY)
			return stored !== null
		} catch (error) {
			return false
		}
	}

	/**
	 * Setup automatic filter persistence
	 * Hooks into filter changes and automatically saves to localStorage
	 * @param {FilterManager} filterManager - The filter manager instance
	 * @param {number} debounceDelay - Debounce delay in milliseconds (default: 300ms)
	 * @returns {Function} - Unsubscribe function to stop auto-saving
	 */
	const setupAutoSave = (filterManager, debounceDelay = 300) => {
		const debouncedSave = createDebouncedSave(filterManager, debounceDelay)

		// Subscribe to filter changes
		const unsubscribe = filterManager.onChange(() => {
			debouncedSave()
		})

		return unsubscribe
	}

	return {
		saveFiltersToLocalStorage,
		createDebouncedSave,
		loadFiltersFromLocalStorage,
		clearFilterPersistence,
		hasPersistedFilters,
		setupAutoSave
	}
}
